//@flow
import includes from 'lodash/includes';
import XLSX from 'xlsx';
import NumberUtils from 'common/utils/NumberUtils';
import Papa from 'papaparse';

class Workbook {
    SheetNames: Array<string>;
    Sheets: Object;

    constructor() {
        this.SheetNames = [];
        this.Sheets = {};
    }
}

export class MultiSheetWorkbookExporter {
    wb: Workbook;
    constructor() {
        this.wb = new Workbook();
    }

    createNewSheet(sheetName: string = 'SHEET 1'): Content {
        if (includes(this.wb.SheetNames, sheetName)) {
            throw new Error('Cant have duplicate sheets');
        }
        this.wb.SheetNames.push(sheetName);
        const content = new Content();
        this.wb.Sheets[sheetName] = content;
        return content;
    }

    sheetFromArrayOfArrays(data: Array<Array<string>>) {
        var ws = {};
        var range = {
            s: {
                c: 10000000,
                r: 10000000
            },
            e: {
                c: 0,
                r: 0
            }
        };
        for (var R = 0; R !== data.length; ++R) {
            for (var C = 0; C !== data[R].length; ++C) {
                if (range.s.r > R) range.s.r = R;
                if (range.s.c > C) range.s.c = C;
                if (range.e.r < R) range.e.r = R;
                if (range.e.c < C) range.e.c = C;
                var cell = {
                    v: data[R][C]
                };
                if (cell.v === null) continue;
                var cellRef = XLSX.utils.encode_cell({
                    c: C,
                    r: R
                });

                if (typeof cell.v === 'number')
                    cell = { ...cell, t: 'n', v: cell.v };
                else if (typeof cell.v === 'boolean') cell.t = 'b';
                else if (typeof cell.v === 'object') cell = { ...cell.v };
                else cell.t = 's';

                ws[cellRef] = cell;
            }
        }
        if (range.s.c < 10000000) ws['!ref'] = XLSX.utils.encode_range(range);
        return ws;
    }

    s2ab(s: string) {
        var buf = new ArrayBuffer(s.length);
        var view = new Uint8Array(buf);
        for (var i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
    }

    getBlob() {
        const { sheetFromArrayOfArrays, wb, s2ab } = this;

        wb.SheetNames.forEach(sheetName => {
            wb.Sheets[sheetName] = sheetFromArrayOfArrays(
                wb.Sheets[sheetName].getContent()
            );
        });

        const wbout = XLSX.write(wb, {
            bookType: 'xlsx',
            bookSST: true,
            type: 'binary'
        });

        return new Blob([s2ab(wbout)], {
            type: 'application/octet-stream'
        });
    }

    getSheetURL() {
        const { sheetFromArrayOfArrays, wb, s2ab } = this;

        wb.SheetNames.forEach(sheetName => {
            wb.Sheets[sheetName] = sheetFromArrayOfArrays(
                wb.Sheets[sheetName].getContent()
            );
        });

        const wbout = XLSX.write(wb, {
            bookType: 'xlsx',
            bookSST: true,
            type: 'binary'
        });

        return {
            uri: URL.createObjectURL(
                new Blob([s2ab(wbout)], {
                    type: 'application/octet-stream'
                })
            ),
            extension: '.xlsx'
        };
    }
}

export class Content {
    content: Array<Array<string>>;

    constructor() {
        this.content = [];
    }

    getContent() {
        return this.content;
    }

    push(...stringArgs: Array<string>) {
        this.twoDimensionalArrayBuilder(stringArgs);
    }

    concat(content: Content) {
        this.content = this.content.concat(content.getContent());
    }

    twoDimensionalArrayBuilder(args: Array<string>) {
        var line = [];
        for (var i = 0; i < args.length; i++) {
            line.push(args[i]);
        }
        this.content.push(line);
    }
}

export default class SpreadSheetExporter {
    static BOMCharacter = '\uFEFF';

    constructor(content, sheetName) {
        this.content = content || []; // If Initialized, 2D arrays only.
        this.sheetName = sheetName || 'Spread Sheet';
        this.wb = new Workbook();
    }

    twoDimensionalArrayBuilder(args) {
        var line = [];
        for (var i = 0; i < args.length; i++) {
            line.push(args[i]);
        }
        this.content.push(line);
    }

    sheetFromArrayOfArrays(data) {
        var ws = {};
        var range = {
            s: {
                c: 10000000,
                r: 10000000
            },
            e: {
                c: 0,
                r: 0
            }
        };
        for (var R = 0; R !== data.length; ++R) {
            for (var C = 0; C !== data[R].length; ++C) {
                if (range.s.r > R) range.s.r = R;
                if (range.s.c > C) range.s.c = C;
                if (range.e.r < R) range.e.r = R;
                if (range.e.c < C) range.e.c = C;
                var cell = {
                    v: data[R][C]
                };
                if (cell.v === null) continue;
                var cellRef = XLSX.utils.encode_cell({
                    c: C,
                    r: R
                });

                if (NumberUtils.isNumeric(cell.v))
                    cell = { ...cell, t: 'n', v: NumberUtils.toNumber(cell.v) };
                else if (typeof cell.v === 'boolean') cell.t = 'b';
                else if (typeof cell.v === 'object') cell = { ...cell.v };
                else cell.t = 's';

                ws[cellRef] = cell;
            }
        }
        if (range.s.c < 10000000) ws['!ref'] = XLSX.utils.encode_range(range);
        return ws;
    }

    s2ab(s) {
        var buf = new ArrayBuffer(s.length);
        var view = new Uint8Array(buf);
        for (var i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
    }

    getCSVData() {
        const { content } = this;
        let csvData = Papa.unparse(content);
        if (!csvData.match(/^data:text\/csv/i)) {
            csvData = `data:attachment/csv;charset=utf-8,${encodeURIComponent(
                csvData
            )}`;
        }

        return {
            uri: csvData,
            extension: '.csv'
        };
    }

    getCSVDataBlob(includeBOMCharacter = true) {
        const { content } = this;
        let csvContent = Papa.unparse(content, {
            escapeChar: '"',
            delimiter: ',',
            newline: '\r\n'
        });

        if (includeBOMCharacter) {
            csvContent = `${SpreadSheetExporter.BOMCharacter}${csvContent}`;
        }

        return new Blob([csvContent], { type: 'text/csv' });
    }

    getBlob() {
        const { sheetFromArrayOfArrays, wb, sheetName, s2ab, content } = this;
        const sheet = sheetFromArrayOfArrays(content);

        wb.SheetNames.push(sheetName);
        wb.Sheets[sheetName] = sheet;

        const wbout = XLSX.write(wb, {
            bookType: 'xlsx',
            bookSST: true,
            type: 'binary'
        });

        return new Blob([s2ab(wbout)], {
            type: 'application/octet-stream'
        });
    }

    getSheetURL() {
        const { sheetFromArrayOfArrays, wb, sheetName, s2ab, content } = this;
        const sheet = sheetFromArrayOfArrays(content);

        wb.SheetNames.push(sheetName);
        wb.Sheets[sheetName] = sheet;

        const wbout = XLSX.write(wb, {
            bookType: 'xlsx',
            bookSST: true,
            type: 'binary'
        });

        return {
            uri: URL.createObjectURL(
                new Blob([s2ab(wbout)], {
                    type: 'application/octet-stream'
                })
            ),
            extension: '.xlsx'
        };
    }
}
